import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Tag, Row, Col, Button, Modal, message, Image } from 'antd';
import { CreditCardOutlined, CalendarOutlined, DollarOutlined, QrcodeOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

const MySubscription = () => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pixModalVisible, setPixModalVisible] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [loadingPix, setLoadingPix] = useState(false);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/organizer-subscription');
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Erro ao carregar assinatura:', error);
      console.error('Error details:', error.response?.data);
      // Set empty data to avoid crashes
      setSubscriptionData({ subscription: null, invoices: [] });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // subscriptions.payment_at (retornado como expires_at pelo backend) ja eh a
  // data de expiracao (pagamento + payment_months). Nao somamos +30 dias.
  const formatDateOrDash = (v) => {
    if (!v) return '-';
    return new Date(v).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'green';
      case 'INACTIVE': return 'red';
      case 'PENDING': return 'orange';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Ativo';
      case 'INACTIVE': return 'Inativo';
      case 'PENDING': return 'Pendente';
      default: return status;
    }
  };

  const invoiceColumns = [
    {
      title: 'Data do Pagamento',
      dataIndex: 'payment_at',
      key: 'payment_at',
      render: (date) => formatDate(date),
    },
    {
      title: 'Valor',
      dataIndex: 'plan_price',
      key: 'plan_price',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Forma de Pagamento',
      key: 'payment_method',
      render: () => 'PIX',
    },
  ];

  const plans = [
    {
      name: 'Mensal',
      price: 29.90,
      months: 1,
      description: 'Pagamento mensal',
      color: '#1890ff'
    },
    {
      name: 'Semestral',
      price: 160.00,
      months: 6,
      description: '6 meses com 12% de desconto',
      color: '#52c41a',
      discount: '12%'
    },
    {
      name: 'Anual',
      price: 288.00,
      months: 12,
      description: '1 ano com 20% de desconto',
      color: '#fa8c16',
      discount: '20%'
    }
  ];

  const handleGeneratePix = async (plan) => {
    setLoadingPix(true);
    try {
      const response = await api.post('/organizer-subscription/generate-pix', {
        pix_value: plan.price,
        plan_months: plan.months,
      });

      setPixData({
        ...response.data,
        planName: plan.name,
        planPrice: plan.price,
      });
      setPixModalVisible(true);
    } catch (error) {
      console.error('Erro ao gerar PIX:', error);
      message.error(error?.response?.data?.message || 'Erro ao gerar PIX para pagamento');
    }
    setLoadingPix(false);
  };

  const handleCopyPixCode = async () => {
    try {
      await navigator.clipboard.writeText(pixData.pix_code);
      message.success('Código PIX copiado!');
    } catch (error) {
      message.error('Erro ao copiar código PIX');
    }
  };

  const { subscription, invoices } = subscriptionData || {};

  return (
    <div>
      <Title level={2}>Minha Assinatura</Title>
      
      <Card style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          <QrcodeOutlined style={{ marginRight: 8 }} />
          Gerar PIX para Pagamento
        </Title>
        
        <Row gutter={[16, 16]}>
          {plans.map((plan, index) => (
            <Col key={index} xs={24} sm={8}>
              <Card
                style={{
                  textAlign: 'center',
                  border: `2px solid ${plan.color}`,
                  position: 'relative'
                }}
              >
                {plan.discount && (
                  <div style={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: '#ff4d4f',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    -{plan.discount}
                  </div>
                )}
                <Title level={4} style={{ color: plan.color, marginBottom: 8 }}>
                  {plan.name}
                </Title>
                <Title level={2} style={{ color: plan.color, marginBottom: 8 }}>
                  {formatCurrency(plan.price)}
                </Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  {plan.description}
                </Text>
                <Button
                  type="primary"
                  style={{ backgroundColor: plan.color, borderColor: plan.color }}
                  loading={loadingPix}
                  onClick={() => handleGeneratePix(plan)}
                  block
                >
                  Gerar PIX
                </Button>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
      
      <Card loading={loading} style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          <CreditCardOutlined style={{ marginRight: 8 }} />
          Assinatura Atual
        </Title>
        
        {subscription ? (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Data de Criação:</Text>
                <br />
                <Text strong>{formatDate(subscription.created_at)}</Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Plano:</Text>
                <br />
                <Text strong>{subscription.plan_name}</Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Valor do Plano:</Text>
                <br />
                <Text strong>{formatCurrency(subscription.plan_price)}</Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Último Pagamento:</Text>
                <br />
                <Text strong>{formatDateOrDash(subscription.last_payment_at)}</Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Status:</Text>
                <br />
                <Tag color={getStatusColor(subscription.status)}>
                  {getStatusText(subscription.status)}
                </Tag>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Data de Expiração:</Text>
                <br />
                <Text strong>{formatDateOrDash(subscription.expires_at || subscription.payment_at)}</Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text type="secondary">Forma de Pagamento:</Text>
                <br />
                <Text strong>PIX</Text>
              </div>
            </Col>
          </Row>
        ) : (
          <Text type="secondary">Nenhuma assinatura encontrada</Text>
        )}
      </Card>

      <Card>
        <Title level={4} style={{ marginBottom: 16 }}>
          <DollarOutlined style={{ marginRight: 8 }} />
          Histórico de Pagamentos
        </Title>
        
        <Table
          columns={invoiceColumns}
          dataSource={invoices}
          rowKey={(record, index) => index}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
          locale={{ emptyText: 'Nenhum pagamento encontrado' }}
        />
      </Card>

      <Modal
        title="PIX Gerado com Sucesso"
        open={pixModalVisible}
        onCancel={() => setPixModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPixModalVisible(false)}>
            Fechar
          </Button>
        ]}
        width={600}
      >
        {pixData && (
          <div style={{ textAlign: 'center' }}>
            <Title level={4} style={{ marginBottom: 16 }}>
              Plano {pixData.planName} - {formatCurrency(pixData.planPrice)}
            </Title>
            
            <div style={{ marginBottom: 24 }}>
              <Image
                src={`data:image/png;base64,${pixData.pix_base64}`}
                alt="QR Code PIX"
                style={{ maxWidth: '200px' }}
              />
            </div>
            
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: 16,
              borderRadius: 6,
              marginBottom: 16,
              border: '1px solid #d9d9d9'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}>
                <Text strong>Código PIX:</Text>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopyPixCode}
                >
                  Copiar
                </Button>
              </div>
              <div style={{
                backgroundColor: 'white',
                padding: 12,
                borderRadius: 4,
                border: '1px solid #e8e8e8',
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all'
              }}>
                {pixData.pix_code}
              </div>
            </div>
            
            <div style={{
              backgroundColor: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 6,
              padding: 16
            }}>
              <Text style={{ color: '#0050b3' }}>
                📱 Você receberá a confirmação do pagamento diretamente no seu WhatsApp!
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MySubscription;